/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { withRouter, useHistory } from "react-router-dom";
import PageLimit from "Components/common/PageLimit";
import Utils from "Utils/utils";
import PaginationComponent from "Components/common/PaginationComponent";
import makeApi, { URLS } from "Utils/apiURL";
import CustomTooltip from "Components/common/CustomTooltip";
import ImportProgressbar from "Components/common/ImportProgressbar";
import ProcessingLoader from "Components/common/ProcessingLoader";
import bulkJobsNotificationStore from "Stores/bulkJobsNotification";
import {
  DATA_TOOLKIT_TOOLS,
  dataToolkitJobRoute
} from "Pages/Dashboard/data-toolkit/dtHelpers";

// Settings > Imports > Data Toolkit: every run on the team (Validate emails / Clean up from Contacts,
// and runs started on the Data Toolkit page). A row opens that run's results.

const statusField = {
  IN_PROGRESS: { status: "In Progress", component: <ProcessingLoader /> },
  TO_BE_PROCESSED: { status: "To be processed", color: "#36D593" },
  PROCESSED: { status: "Completed", color: "#3953FB" },
  FAILED: { status: "Failed", color: "red" }
};

const headerList = [
  { name: "Date", key: "createdDate" },
  { name: "Tool", key: "toolName" },
  { name: "Name", key: "runName" },
  { name: "Progress", key: "contactsProgress" },
  { name: "Status", key: "status" },
  { name: "Result", key: "runResult" },
  { name: "Credits", key: "runCredits" },
  { name: "Owner", key: "ownerName" }
];

const fmt = n => Utils.formatNumberToString(n || 0) || "0";

// Counts reuse the same fields for every tool; the labels differ.
const processedOf = (counts = {}) =>
  (counts.found || 0) +
  (counts.risky || 0) +
  (counts.notFound || 0) +
  (counts.skipped || 0) +
  (counts.failed || 0);

// A finished run still writes its results back to the contacts it ran on.
const effectiveStatus = job =>
  job?.status === "PROCESSED" && job?.writeBackPending
    ? "IN_PROGRESS"
    : job?.status;

const resultText = (job = {}) => {
  const counts = job.counts || {};
  const total = counts.total || job.inputRowCount || 0;
  if (job.status === "FAILED") return job.errorMessage || "Run failed";
  if (job.status !== "PROCESSED") return "--";
  let text;
  switch (job.tool) {
    case "VALIDATE_EMAIL":
      text = `${fmt(counts.found)} valid · ${fmt(counts.risky)} risky · ${fmt(
        counts.notFound
      )} invalid`;
      break;
    case "FIND_EMAIL":
      text = `${fmt(counts.found)} of ${fmt(total)} found`;
      break;
    default:
      text = `${fmt(counts.found)} of ${fmt(total)} enriched`;
  }
  if (job.source === "CONTACTS") {
    text += job.writeBackPending
      ? " · updating contacts"
      : ` · ${fmt(job.contactsUpdated)} contacts updated`;
  }
  return text;
};

const DataToolkitJobs = observer(() => {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showTooltip, setShowTooltip] = useState({});
  const [isJobInprocess, setIsJobInprocess] = useState(false);

  const getJobs = async () => {
    const res = await makeApi({
      url: `${URLS.dataToolkitJobsLog}?pageNo=${pageNo}&limit=${limit}`,
      method: "GET"
    });
    if (res?.data) {
      const {
        jobs = [],
        currentPage = 1,
        totalPages = 1,
        totalCount = 0
      } = res.data || {};
      setData(jobs);
      setPages(totalPages);
      setTotalJobs(totalCount);
      if (currentPage !== pageNo) setPageNo(currentPage);
      setIsJobInprocess(
        jobs.some(j =>
          ["IN_PROGRESS", "TO_BE_PROCESSED"].includes(effectiveStatus(j))
        )
      );
    } else if (res?.response?.status === 401) {
      Utils.showLoggedOutPopup();
    } else {
      Utils.showApiFailureResponse(res);
    }
    setLoader(false);
  };

  useEffect(() => {
    setLoader(true);
    getJobs();
  }, [pageNo, limit]);

  const wasInProgressRef = useRef(false);

  useEffect(() => {
    let interval;
    if (isJobInprocess) {
      wasInProgressRef.current = true;
      interval = setInterval(getJobs, 5000);
    } else if (wasInProgressRef.current) {
      wasInProgressRef.current = false;
      bulkJobsNotificationStore.fetchJobs();
    }
    return () => interval && clearInterval(interval);
  }, [isJobInprocess, pageNo, limit]);

  const openJob = job => history.push(dataToolkitJobRoute(job?.tool, job?.id));

  return (
    <div className="csvImportsSection">
      <div className="tableList">
        <div className="tableHeader tableHeaderEnrich tableDataToolkitJobs">
          {headerList.map(({ name, key }) => (
            <div className={`titleRow ${key}`} key={`dtJobsHead${key}`}>
              {name}
            </div>
          ))}
        </div>
        {!loader ? (
          (data?.length && (
            <div className="tableRecordHeightAlign">
              {data.map(job => {
                const { id, createTs, owner, counts = {} } = job || {};
                const status = effectiveStatus(job);
                const total = counts.total || job.inputRowCount || 0;
                const processed = Math.min(processedOf(counts), total);
                return (
                  <div
                    className="tableBody tableBodyEnrich tableDataToolkitJobs dtJobsRow"
                    key={id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openJob(job)}
                    onKeyDown={e => e.key === "Enter" && openJob(job)}
                  >
                    <div className="createdDate titleRow">
                      {Utils.getDateFromString(createTs || "--")}
                    </div>
                    <div className="toolName titleRow">
                      {DATA_TOOLKIT_TOOLS[job.tool]?.label || "--"}
                    </div>
                    <div className="runName titleRow" title={job.name || ""}>
                      {job.name || "Untitled run"}
                    </div>
                    <div className="contactsProgress titleRow">
                      {status === "IN_PROGRESS" ? (
                        <div className="progressbarWrapper">
                          <ImportProgressbar
                            total={total}
                            processed={processed}
                          />
                        </div>
                      ) : (
                        `${fmt(processed)} / ${fmt(total)}`
                      )}
                    </div>
                    <div className="status">
                      {status === "IN_PROGRESS" ? (
                        <div className="loader-wrapper">
                          {statusField[status]?.component}
                        </div>
                      ) : (
                        <div
                          className="statusDot"
                          style={{
                            backgroundColor: `${statusField[status]?.color}`
                          }}
                        />
                      )}
                      <div>{statusField[status]?.status || "--"}</div>
                    </div>
                    <div className="runResult titleRow" title={resultText(job)}>
                      {resultText(job)}
                    </div>
                    <div className="runCredits titleRow">
                      {fmt(job.creditsCharged)}
                    </div>
                    <div className="ownerName">
                      {(owner && (
                        <div
                          className="contactOwner"
                          onMouseEnter={() =>
                            setShowTooltip({ key: id, value: true })
                          }
                          onMouseLeave={() =>
                            setShowTooltip({ key: id, value: false })
                          }
                        >
                          <span className="circleOwner">
                            {Utils.getOwnerInitial(owner)}
                          </span>
                          {showTooltip?.key === id && showTooltip?.value && (
                            <CustomTooltip
                              text={`Started by ${Utils.getOwnerName(owner)}`}
                            />
                          )}
                        </div>
                      )) ||
                        "--"}
                    </div>
                  </div>
                );
              })}
            </div>
          )) || (
            <div className="noDataFound">
              <i className="material-icons-outlined">list_alt</i>
              <span className="text">No data found</span>
            </div>
          )
        ) : (
          <div className="adjustHt">
            {Array.from({ length: 8 }, (value, index) => (
              <div
                className="tableBodyLoader tableBodyLoaderEnrich tableDataToolkitJobs"
                key={`dtJobsLoader${index}`}
              >
                {headerList.map(({ key }) => (
                  <div className={key} key={`${key}${index}`}>
                    <span className="colLoader linear-background" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="inboxPaginationAndLimit">
        <PaginationComponent
          callback={p => setPageNo(p?.selected + 1 || 1)}
          loading={loader}
          totalPages={pages}
          currentPage={pageNo || 1}
        />
        <PageLimit
          limitPerPage={limit}
          text="Showing"
          cbk={(l = 10) => {
            setLimit(l);
            setPageNo(1);
          }}
          totalCount={totalJobs}
        />
      </div>
    </div>
  );
});

export default withRouter(DataToolkitJobs);
export { DataToolkitJobs };
